import { Children, cloneElement, FC, ReactElement, ReactNode, RefObject, useEffect, useRef } from "react";

import "./Drawer.scss";
import DrawerHeader from "./components/DrawerHeader/DrawerHeader";
import DrawerFooter from "./components/DrawerFooter/DrawerFooter";

export type DrawerProps = {
  /**
   * The content to be displayed inside the drawer.
   */
  children: ReactNode;
  /**
   * Boolean indicating whether the drawer is open.
   */
  isOpen: boolean;
  /**
   * A callback function to be executed when the drawer is closed.
   */
  onClose: () => void;
  /**
   * The size of the drawer. Can be 'xs', 'sm', 'md', or 'lg'.
   * Default is 'md'.
   */
  size?: "xs" | "sm" | "md" | "lg" | "full";
  /**
   * CSS selectors for elements that should be considered "inside" the drawer
   * for click-outside detection. When a click occurs on any of these elements,
   * the drawer will not close. Useful for modals, dropdowns, tooltips, or any
   * other elements rendered outside the drawer's DOM tree.
   */
  excludeSelectors?: string[];
};

const Drawer: FC<DrawerProps> = function (props) {
  const { children, isOpen, onClose, size = "md", excludeSelectors = [] } = props;
  const drawerRef: RefObject<HTMLDivElement> = useRef(null);

  // Close drawer when clicking outside
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;

    // Check if click is inside the drawer
    if (drawerRef.current && drawerRef.current.contains(target)) {
      return;
    }

    // Check if click is inside any excluded elements specified by excludeSelectors
    const isInsideExcludedElement = excludeSelectors.some((selector) => {
      const element = document.querySelector(selector);
      return element && element.contains(target);
    });

    // Only close if click is outside both drawer and excluded elements
    if (!isInsideExcludedElement) {
      onClose();
    }
  };

  // Add handleClickOutside event listener when drawer is open
  useEffect(() => {
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    else document.removeEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const header = Children.toArray(children).find((child: any) => child.type === DrawerHeader);
  const footer = Children.toArray(children).find((child: any) => child.type === DrawerFooter);
  const body = Children.toArray(children).filter((child: any) => child.type !== DrawerHeader && child.type !== DrawerFooter);

  const clonedHeader = header ? cloneElement(header as ReactElement<any>, { onClose }) : null; // Inject onClose prop

  const drawerContainerClass = `drawer drawer-${size} ${isOpen ? "open" : ""}`;

  return (
    <div className={drawerContainerClass} ref={drawerRef}>
      <div className="drawer-content">
        {clonedHeader}
        <div className="drawer-body">{body}</div>
        {footer}
      </div>
    </div>
  );
};

Drawer.defaultProps = {
  size: "md",
  excludeSelectors: [],
};

export default Drawer;
